import React, { useState, useRef, useEffect } from 'react';
import { useJobChat, ChatMessage } from '../hooks/useJobChat';
import {
  useChatColor,
  CHAT_TOKENS as T,
  CHAT_DOT_ONLINE,
  CHAT_ACCENT_FILL,
  CHAT_ACCENT_TEXT,
} from '../hooks/useChatPalette';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import {
  instantTenantDay,
  parseCalendarDate,
  parseInstantDate,
  toCalendarDateString,
  todayCalendarDate,
} from '../lib/calendar-date';
import { effectiveTimeZone } from '../lib/tenant-zone';

interface JobChatDrawerProps {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
  currentUserId: string;
  onNewMessage?: (msg: any) => void;
}

/**
 * A message's time, on the TENANT's clock (companies.time_zone): "today" and
 * "yesterday" are the tenant's days, and the clock time is the tenant's wall
 * clock — not the viewer's device. Same visible format as before (the viewer's
 * locale). An em dash for an unreadable instant or an unknown zone.
 */
function formatTime(dateStr: string, timeZone: string | null) {
  const d = parseInstantDate(dateStr);
  const day = instantTenantDay(dateStr, timeZone);
  const today = todayCalendarDate(timeZone);
  if (!d || !timeZone || !day || !today) return '\u2014';

  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: effectiveTimeZone(timeZone, d) });
  if (day === today) return time;

  const yesterday = parseCalendarDate(today);
  if (yesterday) {
    yesterday.setDate(yesterday.getDate() - 1);
    if (day === toCalendarDateString(yesterday)) return `Yesterday ${time}`;
  }

  return d.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: effectiveTimeZone(timeZone, d) }) + ' ' + time;
}

export default function JobChatDrawer({ jobId, isOpen, onClose, currentUserId, onNewMessage }: JobChatDrawerProps) {
  const handleNewMsg = React.useCallback((msg: any) => {
    if (msg.userId !== currentUserId && onNewMessage) onNewMessage(msg);
  }, [currentUserId, onNewMessage]);
  const { messages, sendMessage, isConnected, isLoading } = useJobChat(isOpen ? jobId : null, handleNewMsg);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [visible, setVisible] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const c = useChatColor();

  // Animate in/out
  useEffect(() => {
    if (isOpen) {
      // Small delay so the transition actually plays
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  async function handleSend() {
    const msg = text.trim();
    if (!msg || sending) return;

    setSending(true);
    try {
      await sendMessage(msg);
      setText('');
    } catch (err) {
      console.error('Send failed:', err);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const isSendDisabled = !text.trim() || sending;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9998,
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}
      />

      {/* Drawer panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: '420px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'hsl(var(--background))',
          borderLeft: '1px solid hsl(var(--border))',
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)',
          borderBottom: '1px solid hsl(var(--border))',
          backgroundColor: 'hsl(var(--secondary))',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 600, color: c('#fff', T.heading), margin: 0 }}>Job Chat</h2>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: isConnected ? CHAT_DOT_ONLINE : c('#6b7280', T.muted),
            }} />
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: c('#9ca3af', T.muted),
              fontSize: '24px',
              cursor: 'pointer',
              lineHeight: 1,
              padding: '4px',
            }}
          >
            &times;
          </button>
        </div>

        {/* Messages area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          {isLoading && (
            <div style={{ textAlign: 'center', color: c('#6b7280', T.muted), padding: '32px 0' }}>
              Loading messages...
            </div>
          )}

          {!isLoading && messages.length === 0 && (
            <div style={{ textAlign: 'center', color: c('#6b7280', T.muted), padding: '32px 0' }}>
              No messages yet. Start the conversation!
            </div>
          )}

          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              isOwn={msg.userId === currentUserId}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <div style={{
          borderTop: '1px solid hsl(var(--border))',
          padding: '12px',
          backgroundColor: 'hsl(var(--secondary))',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* THE ONE ELEMENT IN THIS PANEL THAT WAS ALREADY LEGIBLE IN LIGHT
                MODE, AND NOTHING IN THIS FILE SAID SO. theme.css carries a
                global rule for every input, select and textarea under the light
                theme that forces the fill, the text colour and the border with
                `!important`, and an important declaration in a sheet outranks a
                plain inline one — so the three values below were overridden and
                the field read 14.30:1 while everything around it failed. The
                light halves now name those same tokens, so the two agree
                instead of one silently winning, and removing that global rule
                would no longer take this field down with it.

                The placeholder is NOT covered by that rule: Tailwind's preflight
                claims `input::placeholder` at a higher specificity than the bare
                pseudo-element rule in index.css, so it resolved to a mid-grey in
                both themes and measured 2.32:1 on the light field. The class
                below exists only to give theme.css something to hang a
                light-scoped placeholder rule on. */}
            <input
              type="text"
              className="job-chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              style={{
                flex: 1,
                backgroundColor: c('hsl(var(--background))', T.inputFill),
                border: `1px solid ${c('hsl(var(--border))', T.border)}`,
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '14px',
                color: c('#fff', T.heading),
                outline: 'none',
              }}
            />
            {/* Disabled is a DIFFERENT fill and a different label, not the
                enabled ones diluted. The old treatment dropped opacity to 0.5
                on the whole button, which fades the label toward the fill and
                the fill toward the bar at the same rate: in light mode the
                state change measured 1.30:1 and the word Send measured 1.29:1
                against its own fill — unreadable, in the state this button is
                in whenever the field is empty, which is most of the time.

                A recessed pill with a muted outline and muted text reads as
                disabled and stays legible: the label measures 4.74:1 in dark
                and 5.99:1 in light, and the outline 3.38:1 and 4.86:1 against
                the bar behind it. The FILL alone cannot also clear 3:1 against
                that bar in light, and that is arithmetic rather than an
                omission — the bar sits at luminance 0.80, so a fill 3:1 away
                from it has to be darker than mid-grey, which reads as an
                enabled secondary button rather than a disabled one. WCAG 1.4.11
                exempts disabled controls for exactly this reason. The state
                change is carried by fill, outline, label colour and cursor
                together, not by any one of them. */}
            <button
              onClick={handleSend}
              disabled={isSendDisabled}
              aria-disabled={isSendDisabled}
              style={{
                backgroundColor: isSendDisabled ? T.card : CHAT_ACCENT_FILL,
                color: isSendDisabled ? T.muted : CHAT_ACCENT_TEXT,
                border: isSendDisabled ? `1px solid ${T.muted}` : '1px solid transparent',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: isSendDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Message Bubble ────────────────────────────────────────────────────────

function MessageBubble({ message, isOwn }: { message: ChatMessage; isOwn: boolean }) {
  const c = useChatColor();
  const timeZone = useTenantTimeZone();
  const isDeleted = !!message.deletedAt;
  const userName = [message.user?.firstName, message.user?.lastName].filter(Boolean).join(' ');
  const initials = message.user?.initials || userName.substring(0, 2).toUpperCase();

  return (
    <div style={{
      display: 'flex',
      gap: '8px',
      flexDirection: isOwn ? 'row-reverse' : 'row',
    }}>
      {/* Avatar */}
      {!isOwn && (
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: 'hsl(var(--border))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          fontWeight: 500,
          color: c('#cbd5e1', T.heading),
          flexShrink: 0,
        }}>
          {initials}
        </div>
      )}

      {/* Bubble */}
      <div style={{ maxWidth: '75%' }}>
        {!isOwn && (
          <div style={{ fontSize: '11px', color: c('#9ca3af', T.muted), marginBottom: '2px', paddingLeft: '4px' }}>
            {userName}
          </div>
        )}
        <div style={{
          borderRadius: '12px',
          padding: '8px 12px',
          fontSize: '14px',
          ...(isDeleted
            ? { backgroundColor: c('rgba(30,41,59,0.5)', T.recessed), color: c('#6b7280', T.muted), fontStyle: 'italic' }
            : isOwn
              ? { backgroundColor: CHAT_ACCENT_FILL, color: CHAT_ACCENT_TEXT }
              : { backgroundColor: 'hsl(var(--secondary))', border: '1px solid hsl(var(--border))', color: c('#e2e8f0', T.text) }
          ),
        }}>
          {isDeleted ? 'This message was deleted' : message.message}
        </div>
        <div style={{
          fontSize: '10px',
          color: c('#6b7280', T.muted),
          marginTop: '2px',
          paddingLeft: '4px',
          textAlign: isOwn ? 'right' : 'left',
        }}>
          {formatTime(message.createdAt, timeZone)}
          {message.editedAt && !isDeleted && ' (edited)'}
        </div>
      </div>
    </div>
  );
}
